import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getStoryblokApi } from '@storyblok/react'
import type { ISbStoryData } from '@storyblok/js'
import type { ProductOfferPage } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { env } from '@percent/cause-dashboard/env'
import { isStoryblokPreviewSearch } from '@percent/cause-dashboard/storyblok/helpers/isStoryblokPreviewSearch'
import '../storyblokInit'

export type ProductOfferDetailPageStoryState = {
  story: ISbStoryData<ProductOfferPage> | null
  loading: boolean
  error: Error | null
  isPreview: boolean
  refetch: () => void
}

/**
 * Storyblok `full_slug` for a product-offer CMS page. The URL uses the leaf `slug` only (e.g. `open-ai`);
 * the CDN expects the path under Causes Portal → Product offer pages.
 */
export function productOfferDetailStoryFullSlug(pageLeafSlug: string): string {
  return `causes-portal-pages/product-offer-pages/${pageLeafSlug}`
}

export const STORYBLOK_DETAIL_RESOLVE_RELATIONS = [
  'product_offer_cta_card.offer',
  'native_apply_modal_button.offer'
]

/**
 * Fetches a `product_offer_page` story. `pageLeafSlug` is the story’s leaf slug (matches `story.slug`),
 * same segment as in `/product-offers/:offerDetailSlug`.
 */
export function useProductOfferDetailPageStory(pageLeafSlug: string | undefined): ProductOfferDetailPageStoryState {
  const [searchParams] = useSearchParams()
  const isPreview = isStoryblokPreviewSearch(searchParams)

  const [story, setStory] = useState<ISbStoryData<ProductOfferPage> | null>(null)
  const [loading, setLoading] = useState(Boolean(pageLeafSlug))
  const [error, setError] = useState<Error | null>(null)
  const [fetchId, setFetchId] = useState(0)

  const refetch = useCallback(() => {
    setFetchId(n => n + 1)
  }, [])

  useEffect(() => {
    if (!pageLeafSlug?.trim()) {
      setStory(null)
      setError(null)
      setLoading(false)
      return
    }

    const publishedToken = env.NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN
    const previewToken = env.NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN

    if (isPreview && !previewToken) {
      setStory(null)
      setError(new Error('Storyblok env missing for preview: NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    if (!isPreview && !publishedToken) {
      setStory(null)
      setError(new Error('Storyblok env missing: NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    const token = isPreview ? previewToken : publishedToken
    const version = isPreview ? 'draft' : 'published'
    const fullSlug = productOfferDetailStoryFullSlug(pageLeafSlug.trim())

    let cancelled = false
    setLoading(true)
    setError(null)

    const api = getStoryblokApi()

    api
      .get(`cdn/stories/${fullSlug}`, {
        version,
        resolve_relations: STORYBLOK_DETAIL_RESOLVE_RELATIONS,
        token
      })
      .then(res => {
        if (cancelled) return
        const s = res.data?.story as ISbStoryData<ProductOfferPage> | undefined
        if (!s?.content || s.content.component !== 'product_offer_page') {
          setError(new Error('Storyblok: invalid product_offer_page story response'))
          setStory(null)
          return
        }

        setStory(s)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setStory(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [pageLeafSlug, fetchId, isPreview])

  return { story, loading, error, isPreview, refetch }
}
